import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select"; // NEW CHANGE: Import Select
import { FiX, FiPackage, FiTag, FiBox, FiMapPin, FiShoppingCart, FiDollarSign, FiUser, FiFileText, FiSave, FiBriefcase } from "react-icons/fi";

const API_URL = "https://ims.holdthechild.net";

export default function AddItemModal({ closeModal, onAddItem }) {
    const [formData, setFormData] = useState({
        name: "",
        category: "Office Supplies",
        unitOfMeasure: "units",
        inStock: 0,
        minStockLevel: 10,
        unitPrice: 0,
        location: "",
        notes: "",
        vendorId: "",
        // --- NEW CHANGE: Add Project and Budget Code fields ---
        projectId: null,
        budgetCode: "",
    });
    
    const [vendors, setVendors] = useState([]);
    // --- NEW CHANGE: State for Projects ---
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const modalRef = useRef();

    // Effect to handle clicks outside the modal
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                closeModal();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [closeModal]);

    // --- NEW CHANGE: Fetch Vendors AND Projects on component mount ---
    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const token = localStorage.getItem("token");
                
                // 1. Fetch Vendors
                const vendorResponse = await axios.get(`${API_URL}/api/vendors`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setVendors(vendorResponse.data.data || []);

                // 2. Fetch Projects
                const projectResponse = await axios.get(`${API_URL}/api/projects`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setProjects(projectResponse.data.data || []);

            } catch (error) {
                console.error("Failed to fetch dropdown data:", error);
                toast.error("Failed to load data. 😥");
            }
        };
        fetchDropdownData();
    }, []);

    // --- NEW CHANGE: Effect to auto-fill Budget Code when Project is selected ---
    useEffect(() => {
        if (formData.projectId) {
            const selectedProject = projects.find(p => p._id === formData.projectId);
            if (selectedProject) {
                setFormData(prev => ({
                    ...prev,
                    budgetCode: selectedProject.budgetCode || "",
                }));
            }
        } else {
            // Clear budget code if project is cleared
            setFormData(prev => ({ ...prev, budgetCode: "" }));
        }
    }, [formData.projectId, projects]);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "number" ? parseFloat(value) : value,
        }));
    };

    // --- NEW CHANGE: Handle Project Selection ---
    const handleProjectChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            projectId: selectedOption ? selectedOption.value : null,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const token = localStorage.getItem("token");

        const submissionData = {
            ...formData,
            inStock: Number(formData.inStock),
            minStockLevel: Number(formData.minStockLevel),
            unitPrice: Number(formData.unitPrice),
            vendor: formData.vendorId || null,
            // --- NEW CHANGE: Include projectId in submission ---
            projectId: formData.projectId || null,
        };

        try {
            await axios.post(`${API_URL}/api/inventory`, submissionData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Inventory item added successfully! 🎉");
            onAddItem();
            closeModal();
        } catch (error) {
            const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
            toast.error(`Error: ${errorMessage} 😥`);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Prepare Options for Select Dropdowns
    const categoryOptions = [
        { value: "Office Supplies", label: "Office Supplies" },
        { value: "Food & Hygiene", label: "Food & Hygiene" },
        { value: "Electronics", label: "Electronics" },
        { value: "Stationery", label: "Stationery" },
        { value: "Other", label: "Other" },
    ];

    const vendorOptions = [{ value: "", label: "Select a Vendor" }, ...vendors.map(v => ({ value: v._id, label: v.name }))];

    // --- NEW CHANGE: Prepare Project Options ---
    const projectOptions = projects.map(p => ({ value: p._id, label: p.projectTitle }));

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
            <div 
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl relative transform transition-all duration-300 ease-out flex flex-col max-h-[90vh]"
            >
                {/* Sticky Header with Orange Theme */}
                <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500 sticky top-0 z-10">
                    <div className="flex justify-between items-center">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                <FiPackage className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                            </div>
                            <div>
                                <h2 className="text-lg sm:text-xl font-bold text-black">
                                    Add New Inventory Item
                                </h2>
                                <div className="flex items-center mt-1 text-black text-sm">
                                    <FiTag className="w-3 h-3 mr-1" />
                                    <span>Operations Department</span>
                                </div>
                            </div>
                        </div>
                        <button 
                            onClick={closeModal} 
                            className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                        >
                            <FiX size={20} />
                        </button>
                    </div>
                </div>

                {/* Scrollable Form Body */}
                <div className="p-4 sm:p-6 overflow-y-auto flex-grow">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Item Details Section */}
                        <div className="border border-gray-200 rounded-lg p-4 sm:p-5">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FiPackage className="h-5 w-5 mr-2 text-orange-500" />
                                Item Details
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                {/* Item Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Item Name <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiPackage className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="e.g., A4 Paper, Pens"
                                        />
                                    </div>
                                </div>

                                {/* Category */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Category <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiTag className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                        >
                                            {categoryOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {/* Unit of Measure */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Unit of Measure
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiBox className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="unitOfMeasure"
                                            value={formData.unitOfMeasure}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="e.g., units, boxes, reams"
                                        />
                                    </div>
                                </div>

                                {/* Storage Location */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Storage Location <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiMapPin className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="e.g., Main Storeroom"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* --- NEW CHANGE: Project Assignment Section --- */}
                        <div className="border border-gray-200 rounded-lg p-4 sm:p-5">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FiBriefcase className="h-5 w-5 mr-2 text-orange-500" />
                                Project Assignment (Optional)
                            </h3>
                            <div className="grid grid-cols-1 gap-4 sm:gap-5">
                                {/* Project Selector */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Link to Project
                                    </label>
                                    <Select
                                        options={projectOptions}
                                        value={projectOptions.find(opt => opt.value === formData.projectId)}
                                        onChange={handleProjectChange}
                                        placeholder="Select a Project..."
                                        isClearable
                                        className="react-select-container" 
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '44px',
                                                borderColor: '#d1d5db',
                                                '&:hover': { borderColor: '#f97316' },
                                                '&:focus-within': {
                                                    borderColor: '#f97316',
                                                    boxShadow: '0 0 0 2px rgba(249, 115, 22, 0.2)',
                                                },
                                            }),
                                        }}
                                    />
                                </div>

                                {/* Budget Code Display */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Budget Code (Auto-Pulled)
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiTag className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={formData.budgetCode}
                                            readOnly
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 bg-orange-50 text-orange-800 font-mono focus:outline-none"
                                            placeholder="Select a project to see code"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stock & Cost Section */}
                        <div className="border border-gray-200 rounded-lg p-4 sm:p-5">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FiShoppingCart className="h-5 w-5 mr-2 text-orange-500" />
                                Stock & Cost
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                {/* Initial Stock Quantity */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Initial Stock Quantity <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiShoppingCart className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="inStock"
                                            value={formData.inStock}
                                            onChange={handleChange}
                                            min="0"
                                            required
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>

                                {/* Minimum Stock Level */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Minimum Stock Level <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiShoppingCart className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="minStockLevel"
                                            value={formData.minStockLevel}
                                            onChange={handleChange}
                                            min="0"
                                            required
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="e.g., 10"
                                        />
                                    </div>
                                </div>

                                {/* Unit Price */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Unit Price ($) <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiDollarSign className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="unitPrice"
                                            value={formData.unitPrice}
                                            onChange={handleChange}
                                            min="0"
                                            step="0.01"
                                            required
                                            className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>

                                {/* Vendor */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Vendor
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiUser className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <select
                                            name="vendorId"
                                            value={formData.vendorId}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                        >
                                            {vendorOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Notes Section */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes
                            </label>
                            <div className="relative">
                                <div className="absolute top-3 left-0 pl-3 flex items-start pointer-events-none">
                                    <FiFileText className="h-5 w-5 text-gray-400 mt-0.5" />
                                </div>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows="3"
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                    placeholder="Any additional information about the item..."
                                />
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                            <button
                                type="button"
                                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all duration-200 order-2 sm:order-1"
                                onClick={closeModal}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2 order-1 sm:order-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Adding...</span>
                                    </>
                                ) : (
                                    <>
                                        <FiSave className="w-4 h-4" />
                                        <span>Add Item</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}